import React, { PropsWithChildren, ReactNode, createContext, useContext } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { Stack } from '../../stack';
import { Divider } from '../../divider';

export interface PanelTabsContextProps {
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export const PanelTabContext = createContext<PanelTabsContextProps | null>(null);

export const usePanelTabContext = () => {
  const context = useContext(PanelTabContext);

  if (!context) {
    throw new Error('PanelTabContext is not defined');
  }
  return context;
};

export interface TabsProps {
  title?: string;
  important?: boolean;
  items: ReactNode;
}

export interface PanelTabsProps {
  divider?: boolean;
  className?: string;
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export const PanelTabs = ({
  divider,
  children,
  className,
  activeTabId,
  onChange,
}: PropsWithChildren<PanelTabsProps>) => {
  return (
    <PanelTabContext.Provider value={{ activeTabId, onChange }}>
      <ScrollContainer
        direction='row'
        className={classNames('pds-panel-tabs', className)}
        divider={Boolean(divider)}
        role='tablist'
      >
        {React.Children.map(children, (child, i) => {
          if (i > 0 && divider)
            return (
              <>
                <StyledDivider direction='vertical' aria-hidden='true' data-testid='panel-tabs-divider' />
                {child}
              </>
            );
          return child;
        })}
      </ScrollContainer>
    </PanelTabContext.Provider>
  );
};

const ScrollContainer = styled(Stack)<{ divider: boolean }>`
  position: relative;
  display: flex;
  margin: 0;
  overflow-x: scroll;
  scrollbar-width: none;
  gap: ${({ divider }) => (divider ? '12px' : '8px')};

  ::-webkit-scrollbar {
    display: none;
  }
`;

const StyledDivider = styled(Divider)`
  flex-shrink: 0;
  height: auto;
`;
